import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Package, ShoppingBag, DollarSign } from 'lucide-react';
import { ProductsManagement } from '../components/admin/ProductsManagement';
import { OrdersManagement } from '../components/admin/OrdersManagement';
import { CompletedOrdersTab } from '../components/admin/CompletedOrdersTab';
import { BusinessHoursTab } from '../components/admin/BusinessHoursTab';
import { ExchangeRateTab } from '../components/admin/ExchangeRateTab';
import { ReviewsTab } from '../components/admin/ReviewsTab';
import { GalleryTab } from '../components/admin/GalleryTab';
import { PromotionsTab } from '../components/admin/PromotionsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Admin = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);

  useEffect(() => {
    fetchProducts();
    fetchOrders();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/products`);
      if (response.ok) setProducts(await response.json());
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/orders`);
      if (response.ok) {
        const allOrders = await response.json();
        const active = allOrders.filter(order => 
          order.status !== 'delivered' && order.status !== 'cancelled'
        );
        const completed = allOrders.filter(order => 
          order.status === 'delivered' || order.status === 'cancelled'
        );
        setOrders(active);
        setCompletedOrders(completed);
        
        const revenue = completed.reduce((sum, order) => {
          if (order.payment_status === 'pagado') {
            return sum + order.total_amount;
          }
          return sum;
        }, 0);
        setTotalRevenue(revenue);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-white mb-8">Panel de Administración</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Productos</p>
                  <p className="text-3xl font-bold text-white">{products.length}</p>
                </div>
                <Package className="h-10 w-10 text-cyan-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Pedidos Totales</p>
                  <p className="text-3xl font-bold text-white">{orders.length}</p>
                </div>
                <ShoppingBag className="h-10 w-10 text-emerald-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Ingresos</p>
                  <p className="text-3xl font-bold text-white">${totalRevenue.toLocaleString()}</p>
                </div>
                <DollarSign className="h-10 w-10 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700 flex-wrap h-auto">
            <TabsTrigger value="products" className="data-[state=active]:bg-cyan-600">
              Productos
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-cyan-600">
              Pedidos Activos
            </TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-cyan-600">
              Pedidos Completados
            </TabsTrigger>
            <TabsTrigger value="promotions" className="data-[state=active]:bg-cyan-600">
              Promociones
            </TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-cyan-600">
              Galería
            </TabsTrigger>
            <TabsTrigger value="hours" className="data-[state=active]:bg-cyan-600">
              Horarios
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-cyan-600">
              Opiniones
            </TabsTrigger>
            <TabsTrigger value="cotizacion" className="data-[state=active]:bg-cyan-600">
              Cotización Dólar
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products" key="products">
            <ProductsManagement 
              products={products}
              onRefresh={fetchProducts}
            />
          </TabsContent>

          <TabsContent value="orders" key="orders">
            <OrdersManagement 
              orders={orders}
              onRefresh={fetchOrders}
            />
          </TabsContent>

          <TabsContent value="completed" key="completed">
            <CompletedOrdersTab 
              completedOrders={completedOrders}
              onOrdersUpdate={fetchOrders}
            />
          </TabsContent>

          <TabsContent value="promotions" key="promotions">
            <PromotionsTab products={products} />
          </TabsContent>

          <TabsContent value="gallery" key="gallery">
            <GalleryTab />
          </TabsContent>

          <TabsContent value="hours" key="hours">
            <BusinessHoursTab />
          </TabsContent>

          <TabsContent value="reviews" key="reviews">
            <ReviewsTab />
          </TabsContent>

          <TabsContent value="cotizacion" key="cotizacion">
            <ExchangeRateTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
